import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { useAuth } from "../../contexts/AuthContext";
import { List, NewList } from "../../types";
import { listEmojiOrDefault } from "../../constants/defaults";

export function useCreateList() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (newList: NewList): Promise<List> => {
      if (!user) throw new Error("Not authenticated");

      const currentLists = queryClient.getQueryData<List[]>(queryKeys.lists) ?? [];
      const maxOrderPosition =
        currentLists.length > 0
          ? Math.max(...currentLists.map((l) => l.order_position))
          : 0;

      const { data, error } = await supabase
        .from("lists")
        .insert({
          user_id: user.id,
          name: newList.name,
          emoji: listEmojiOrDefault(newList.emoji),
          order_position: maxOrderPosition + 100,
        })
        .select()
        .single();

      if (error) throw error;
      return data as List;
    },

    onSuccess: (created) => {
      queryClient.setQueryData<List[]>(queryKeys.lists, (old) => [
        ...(old ?? []),
        created,
      ]);
    },

    onError: (err) => {
      Sentry.captureException(err, { extra: { mutation: "useCreateList" } });
      toast.error("Failed to create list. Please try again.");
    },
  });
}
